/**
 * 문제 풀이 중 쌓인 Recoil 상태를 초기화하는 훅.
 *
 * 로그아웃, 챕터 이탈, 챕터 전환처럼 "이전 풀이 흔적을 지워야 하는" 화면 전환에서 쓴다.
 * 어디까지 지울지에 따라 두 함수 중 하나를 고른다.
 *   - `clearAllQuizState`: 로그아웃, 완전히 새로운 챕터 진입 시 전부 초기화.
 *   - `clearChapterProgressState`: 챕터 전환 시 진행 상태만 초기화(새 챕터 데이터 보존).
 */
import { useResetRecoilState } from "recoil";
import {
  problemDataState,
  tempDataState,
  answerDataState,
  isAnswerSubmittedState,
  retryState,
  problemPauseState,
  unansweredQuestionsState,
  currentQuestionIndexState,
  chapterDetailState,
  chapterTimeState,
  chapterNameState,
  currentChapterIdState,
} from "@/recoil/atom";

export const useClearQuizState = () => {
  const resetProblemData = useResetRecoilState(problemDataState);
  const resetTempData = useResetRecoilState(tempDataState);
  const resetAnswerData = useResetRecoilState(answerDataState);
  const resetIsAnswerSubmitted = useResetRecoilState(isAnswerSubmittedState);
  const resetRetry = useResetRecoilState(retryState);
  const resetProblemPause = useResetRecoilState(problemPauseState);
  const resetUnansweredQuestions = useResetRecoilState(unansweredQuestionsState);
  const resetCurrentQuestionIndex = useResetRecoilState(currentQuestionIndexState);
  const resetChapterDetail = useResetRecoilState(chapterDetailState);
  const resetChapterTime = useResetRecoilState(chapterTimeState);
  const resetChapterName = useResetRecoilState(chapterNameState);
  const resetCurrentChapterId = useResetRecoilState(currentChapterIdState);

  /** 문제 풀이 관련 Recoil 상태를 전부 초기화한다. 로그아웃, 완전히 새로운 챕터 진입 시 사용. */
  const clearAllQuizState = () => {
    resetProblemData();
    resetTempData();
    resetAnswerData();
    resetIsAnswerSubmitted();
    resetRetry();
    resetProblemPause();
    resetUnansweredQuestions();
    resetCurrentQuestionIndex();
    resetChapterDetail();
    resetChapterTime();
    resetChapterName();
    resetCurrentChapterId();
  };

  // 챕터 전환 시 한 챕터 풀이 진행 상태만 초기화한다.
  // clearAllQuizState와 달리 problemData/chapterDetail/chapterName/currentChapterId는
  // 건드리지 않는다. 호출부가 방금 세팅한 새 챕터 데이터를 지우지 않기 위함.
  const clearChapterProgressState = () => {
    resetTempData();
    resetAnswerData();
    resetIsAnswerSubmitted();
    resetRetry();
    resetProblemPause();
    resetUnansweredQuestions();
    resetCurrentQuestionIndex();
    resetChapterTime();
  };

  return { clearAllQuizState, clearChapterProgressState };
};
